import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';
import speciesName from '../utils/speciesName';
import debouncedSearch from '../utils/debouncedSearch';
/**
 * Compact species-by-name autocomplete for a single form field - same
 * /api/species-autocomplete endpoint and debounce/results-list behavior as
 * SpeciesSearchWidget, but without that component's card/label chrome
 * (it's meant to sit inline inside a larger form, e.g.
 * SpeciesCorrectionsPage.js's gbifId fields, not stand alone in a
 * sidebar) - built as its own component rather than adding an
 * "inline mode" prop to SpeciesSearchWidget, since the two have
 * different enough markup that threading a flag through would be messier
 * than just having two small components.
 *
 * Deliberately never lets the caller's numeric id go stale: `onselect`
 * only fires from an actual pick in the dropdown, and any further typing
 * calls `onclear` first - so "type a name, don't select, submit anyway"
 * can't silently submit a previously-selected id that no longer matches
 * what's on screen.
 */
export default class SpeciesGbifSearchField extends Component {
  query = '';
  results = [];
  loading = false;
  open = false;
  gbifUnavailable = false;
  debounceHandle = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.query = this.attrs.initialQuery || '';
    this.results = [];
    this.loading = false;
    this.open = false;
    this.debounceHandle = null;
  }

  // resetKey lets the parent force this field back to a fresh state (e.g.
  // switching from "editing row A" to "editing row B", or back to "add")
  // without recreating the component - see SpeciesCorrectionsPage.js.
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    if (vnode.attrs.resetKey !== this.attrs.resetKey) {
      this.query = vnode.attrs.initialQuery || '';
      this.results = [];
      this.open = false;
    }
  }
  view() {
    return m("div", {
      className: "SpeciesGbifSearchField"
    }, m("div", {
      className: "SpeciesGbifSearchField-input"
    }, m("input", {
      className: "FormControl",
      type: "text",
      placeholder: extractText(this.attrs.placeholder),
      "aria-label": extractText(this.attrs.label || this.attrs.placeholder),
      value: this.query,
      oninput: e => this.onInput(e.target.value),
      onfocus: () => {
        this.open = this.results.length > 0;
      },
      onblur: () => {
        // Closes the list when the field loses focus to anything else: a
        // press inside the list doesn't blur it (the list's onmousedown
        // below), so a click there completes however long the button is
        // held. The delay alone used to drop slow clicks - the list was
        // gone before the mouseup (reported 2026-10-12).
        setTimeout(() => {
          this.open = false;
          m.redraw();
        }, 150);
      },
      required: this.attrs.required
    }), this.loading && m(LoadingIndicator, {
      display: "inline",
      size: "small"
    })), this.open && this.results.length > 0 && m("ul", {
      className: "SpeciesGbifSearchField-results",
      onmousedown: e => e.preventDefault()
    }, this.gbifUnavailableNotice(), this.results.map(s => m("li", {
      key: s.id,
      onclick: () => this.select(s)
    }, speciesName(s), " ", m("small", null, "\xB7 ", s.status)))), this.open && !this.loading && this.results.length === 0 && m("ul", {
      className: "SpeciesGbifSearchField-results",
      onmousedown: e => e.preventDefault()
    }, this.gbifUnavailableNotice(), m("li", {
      className: "SpeciesGbifSearchField-empty"
    }, app.translator.trans('champis-species.forum.search.empty'))));
  }
  gbifUnavailableNotice() {
    return this.gbifUnavailable && m("li", {
      className: "SpeciesGbifSearchField-notice"
    }, app.translator.trans('champis-species.forum.search.gbif_unavailable'));
  }
  onInput(value) {
    this.query = value;
    this.open = false;
    this.attrs.onclear();
    debouncedSearch(this, value, query => this.search(query));
  }
  search(query) {
    this.loading = true;
    m.redraw();
    const filter = {
      query
    };
    if (this.attrs.highertaxon) filter.highertaxon = this.attrs.highertaxon;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/species-autocomplete',
      params: {
        filter
      }
    }).then(response => {
      // Stale response for a query the user has since changed/cleared.
      if (query !== this.query.trim()) return;
      this.results = (response.data || []).map(item => ({
        id: item.id,
        ...item.attributes
      }));
      this.gbifUnavailable = !!response.meta?.gbifUnavailable;
      this.loading = false;
      this.open = true;
      m.redraw();
    }).catch(() => {
      this.loading = false;
      m.redraw();
    });
  }
  select(species) {
    this.query = species.scientificName || '';
    this.open = false;
    this.attrs.onselect(species);
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesGbifSearchField', SpeciesGbifSearchField);